import moment from 'moment';
import { AppLogLevel } from '@/models/enums';
import { AppError, LoggerSetting, Message } from '@/models/log.models';
import { actions } from '@/models/store.actions';
import { isDevelopment } from '@/helpers/utility';
import store from '@/store/store';

interface ILogToStoreResult {
  message?: string;
  storeMessageId?: string;
}

interface ILogResult {
  logToStoreResult?: ILogToStoreResult;
}

class Logger {
  private get loggerSettings(): LoggerSetting {
    return store.getters[actions.LOG_GET_SETTINGS];
  }

  private get consoleLogLevel(): AppLogLevel {
    return AppLogLevel[this.loggerSettings?.consoleLogLevel];
  }

  private get storageLogLevel(): AppLogLevel {
    return AppLogLevel[this.loggerSettings?.storageLogLevel];
  }

  debug(message?: any, ...optionalParams: any[]): ILogResult {
    return this.log(AppLogLevel.Debug, message, ...optionalParams);
  }

  info(message?: any, ...optionalParams: any[]): ILogResult {
    return this.log(AppLogLevel.Info, message, ...optionalParams);
  }

  warn(message?: any, ...optionalParams: any[]): ILogResult {
    return this.log(AppLogLevel.Warning, message, ...optionalParams);
  }

  error(message?: any, ...optionalParams: any[]): ILogResult {
    return this.log(AppLogLevel.Error, message, ...optionalParams);
  }

  private log(logLevel: AppLogLevel, message?: any, ...optionalParams: any[]): ILogResult {
    let ret: ILogResult = {};
    if (typeof message !== 'undefined' || optionalParams.length) {
      this.setLoggerSettingsIfEmpty();

      this.logToConsole(logLevel, message, ...optionalParams);

      ret.logToStoreResult = this.logToStore(logLevel, message, ...optionalParams);
    }
    return ret;
  }

  private logToConsole(logLevel: AppLogLevel, message?: any, ...optionalParams: any[]): void {
    if (this.consoleLogLevel >= logLevel) {
      let msg;
      if (typeof message === 'string') {
        msg = message;
      } else if (typeof message !== 'undefined') {
        optionalParams = [message, ...optionalParams];
      }
      const logFunc = this.getConsoleLoggerFunc(logLevel);
      logFunc(
        `${moment().format('YYYY-MM-DD HH:mm:ss.SSS')} ` + `%c[${AppLogLevel[logLevel]}]%c ` + (msg || ''),
        `color: ${this.getConsoleColor(logLevel)}; font-weight: bold;`,
        '',
        ...optionalParams);
    }
  }

  private logToStore(logLevel: AppLogLevel, message?: any, ...optionalParams: any[]): ILogToStoreResult | undefined {
    let ret: ILogToStoreResult | undefined;
    if (this.storageLogLevel >= logLevel) {
      let appError;
      let data;
      let msg;
      if (logLevel === AppLogLevel.Error) {
        if (typeof message !== 'undefined') {
          if (typeof message === 'string') {
            msg = message;
          } else if (message instanceof Error) {
            appError = new AppError(message as any);
          } else if (message instanceof AppError) {
            appError = message;
          } else if ('error' in message) {
            msg = message.error;
          }
        }

        if (optionalParams.length) {
          if (!appError) {
            let err = optionalParams[0];
            if (err && typeof err === 'object') {
              if (err instanceof AppError) {
                appError = err;
              } else if (err instanceof Error) {
                appError = new AppError(err as any);
              }
            }
            if (appError) {
              optionalParams.shift();
            }
            if (!msg && typeof message !== 'undefined') {
              optionalParams = [message, ...optionalParams];
            }
          }
          data = optionalParams;
        }
      } else {
        if (typeof message !== 'undefined') {
          if (typeof message === 'string') {
            msg = message;
          } else {
            optionalParams = [message, ...optionalParams];
          }
        }
        data = optionalParams;
      }
      msg = msg || '';
      const logMessage = new Message(logLevel, msg, appError, data);
      store.commit(actions.LOG_MUT_MESSAGE, { message: logMessage });

      ret = {
        message: logMessage.text,
        storeMessageId: logMessage.id
      };
    }
    return ret;
  }

  private getConsoleLoggerFunc(logLevel: AppLogLevel): Function {
    let f = console.log;
    if (logLevel === AppLogLevel.Warning) {
      f = console.warn;
    } else if (logLevel === AppLogLevel.Error) {
      f = console.error;
    }
    return f;
  }

  private getConsoleColor(logLevel: AppLogLevel): string {
    let c = '';
    if (logLevel === AppLogLevel.Debug) {
      c = '#48C9B0';
    } else if (logLevel === AppLogLevel.Info) {
      c = '#1A5276';
    }
    return c;
  }

  private setLoggerSettingsIfEmpty(): void {
    if (!this.loggerSettings) {
      let consoleLogLevel = isDevelopment()
        ? AppLogLevel[AppLogLevel.Debug] : AppLogLevel[AppLogLevel.Warning];
      store.commit(
        actions.LOG_MUT_SETTINS,
        {
          consoleLogLevel,
          storageLogLevel: AppLogLevel[AppLogLevel.Error]
        }
      );
    }
  }
}

export const logger: Logger = new Logger();

export default {
  install(Vue) {
    Vue.prototype.logger = logger;
  }
};
